'use client';
import React, { useState } from 'react';
import {
  Receipt, Download, FileText, CheckCircle, Clock, AlertCircle,
  RefreshCw, FileSpreadsheet, ChevronDown, ChevronUp, RotateCcw,
  ShieldAlert, ExternalLink,
} from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface InvoiceOrder {
  id: string;
  orderNumber: string;
  issueLabel: string;
  placement: string;
  regions: string[];
  term: 'one' | 'three';
  total: number;
  paymentStatus: 'paid' | 'pending' | 'failed' | 'refunded' | 'partially_refunded';
  orderStatus: 'active' | 'completed' | 'processing';
  artworkDeadline: string;
  campaignDeliveryDate: string;
  createdAt: string;
  paymentIntentId?: string | null;
  refundAmount?: number | null;
  refundedAt?: string | null;
  disputeId?: string | null;
  disputeStatus?: string | null;
  invoiceUrl?: string | null;
}

interface InvoicesSectionProps {
  orders: InvoiceOrder[];
  loading: boolean;
}

const PLACEMENT_NAMES: Record<string, string> = {
  standard: 'Standard Inside Page',
  half_page: 'Half Page',
  ifc: 'Inside Front Cover',
  ibc: 'Inside Back Cover',
  bc: 'Back Cover',
  fc: 'Front Cover',
};

const paymentBadge: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
  paid: {
    label: 'Paid',
    className: 'bg-green-500/15 text-green-400 border border-green-500/25',
    icon: <CheckCircle size={10} />,
  },
  pending: {
    label: 'Pending',
    className: 'bg-amber-500/15 text-amber-400 border border-amber-500/25',
    icon: <Clock size={10} />,
  },
  failed: {
    label: 'Failed',
    className: 'bg-red-500/15 text-red-400 border border-red-500/25',
    icon: <AlertCircle size={10} />,
  },
  refunded: {
    label: 'Refunded',
    className: 'bg-purple-500/15 text-purple-400 border border-purple-500/25',
    icon: <RotateCcw size={10} />,
  },
  partially_refunded: {
    label: 'Partial Refund',
    className: 'bg-orange-500/15 text-orange-400 border border-orange-500/25',
    icon: <RotateCcw size={10} />,
  },
};

function PDFDownloadButton({ order }: { order: InvoiceOrder }) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');

  // If a stored invoice URL exists, use it directly
  if (order.invoiceUrl) {
    return (
      <a
        href={order.invoiceUrl}
        target="_blank"
        rel="noopener noreferrer"
        title="Download PDF Invoice"
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-600/15 border border-brand-600/25 text-brand-300 hover:bg-brand-600/25 hover:text-white text-xs font-medium transition-all"
      >
        <FileText size={12} />
        PDF
        <ExternalLink size={10} className="opacity-60" />
      </a>
    );
  }

  const handleDownload = async () => {
    setStatus('loading');
    try {
      const supabase = createClient();
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
      const res = await fetch(`${supabaseUrl}/functions/v1/generate-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ orderId: order.id }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }

      const blob = new Blob([await res.arrayBuffer()], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ZipAdBook-Invoice-${order.orderNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setStatus('idle');
    } catch (err) {
      console.error('PDF download failed:', err);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <button
      onClick={handleDownload}
      disabled={status === 'loading'}
      title={status === 'error' ? 'Download failed — try again' : 'Download PDF Invoice'}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-60 ${
        status === 'error' ?'bg-red-500/15 border border-red-500/25 text-red-400' :'bg-brand-600/15 border border-brand-600/25 text-brand-300 hover:bg-brand-600/25 hover:text-white'
      }`}
    >
      {status === 'loading' ? (
        <RefreshCw size={12} className="animate-spin" />
      ) : status === 'error' ? (
        <AlertCircle size={12} />
      ) : (
        <FileText size={12} />
      )}
      {status === 'loading' ? 'Generating…' : status === 'error' ? 'Failed' : 'PDF'}
    </button>
  );
}

function exportCSV(orders: InvoiceOrder[]) {
  const headers = [
    'Order Number',
    'Issue',
    'Placement',
    'Regions',
    'Term',
    'Total ($)',
    'Payment Status',
    'Refund Amount ($)',
    'Refunded At',
    'Dispute ID',
    'Dispute Status',
    'Order Status',
    'Artwork Deadline',
    'Campaign Delivery',
    'Order Date',
    'Transaction ID',
    'Invoice URL',
  ];

  const rows = orders.map((o) => [
    o.orderNumber,
    `"${o.issueLabel}"`,
    `"${PLACEMENT_NAMES[o.placement] || o.placement}"`,
    `"${o.regions.join('; ')}"`,
    o.term === 'three' ? '3-Issue Campaign' : 'Single Issue',
    o.total,
    o.paymentStatus,
    o.refundAmount != null ? (o.refundAmount / 100).toFixed(2) : '',
    o.refundedAt || '',
    o.disputeId || '',
    o.disputeStatus || '',
    o.orderStatus,
    o.artworkDeadline,
    o.campaignDeliveryDate,
    o.createdAt,
    o.paymentIntentId || '',
    o.invoiceUrl || '',
  ]);

  const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const now = new Date();
  const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  a.download = `ZipAdBook-OrderHistory-${dateStr}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function InvoicesSection({ orders, loading }: InvoicesSectionProps) {
  const [collapsed, setCollapsed] = useState(false);

  const paidOrders = orders.filter((o) => o.paymentStatus === 'paid');
  const refundedOrders = orders.filter((o) => o.paymentStatus === 'refunded' || o.paymentStatus === 'partially_refunded');
  const disputedOrders = orders.filter((o) => o.disputeId);
  const totalPaid = paidOrders.reduce((s, o) => s + (o.total || 0), 0);
  const storedInvoices = orders.filter((o) => o.invoiceUrl).length;

  return (
    <div className="glass-card rounded-2xl border border-white/8 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-5 border-b border-white/8 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-green-500/15 border border-green-500/20 flex items-center justify-center shrink-0">
            <Receipt size={17} className="text-green-400" />
          </div>
          <div>
            <h2 className="font-semibold text-white text-sm">Invoices & Billing History</h2>
            <p className="text-xs text-slate-400 mt-0.5">Download PDF receipts or export order history for accounting</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {orders.length > 0 && (
            <button
              onClick={() => exportCSV(orders)}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-white/5 border border-white/10 text-xs font-medium text-slate-300 hover:bg-white/10 hover:text-white transition-all"
              title="Export all orders as CSV for accounting/reconciliation"
            >
              <FileSpreadsheet size={13} />
              Export CSV
            </button>
          )}
          <button
            onClick={() => setCollapsed((v) => !v)}
            className="flex items-center gap-1 px-2.5 py-2 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-400 hover:text-white hover:bg-white/10 transition-all"
            aria-label={collapsed ? 'Expand invoices' : 'Collapse invoices'}
          >
            {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          {/* Summary strip */}
          {!loading && orders.length > 0 && (
            <div className="px-6 py-3 bg-white/2 border-b border-white/6 flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Total Invoices:</span>
                <span className="text-xs font-semibold text-white">{orders.length}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Paid:</span>
                <span className="text-xs font-semibold text-green-400">{paidOrders.length}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Total Paid:</span>
                <span className="text-xs font-semibold text-white">${totalPaid.toLocaleString()}</span>
              </div>
              {storedInvoices > 0 && (
                <div className="flex items-center gap-2">
                  <FileText size={11} className="text-brand-400" />
                  <span className="text-xs text-slate-500">PDF Invoices:</span>
                  <span className="text-xs font-semibold text-brand-300">{storedInvoices} ready</span>
                </div>
              )}
              {refundedOrders.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">Refunds:</span>
                  <span className="text-xs font-semibold text-purple-400">{refundedOrders.length}</span>
                </div>
              )}
              {disputedOrders.length > 0 && (
                <div className="flex items-center gap-2">
                  <ShieldAlert size={11} className="text-amber-400" />
                  <span className="text-xs text-slate-500">Disputes:</span>
                  <span className="text-xs font-semibold text-amber-400">{disputedOrders.length}</span>
                </div>
              )}
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-7 h-7 border-2 border-brand-500/30 border-t-brand-400 rounded-full animate-spin" />
            </div>
          ) : orders.length === 0 ? (
            <div className="py-12 text-center">
              <Receipt size={32} className="text-slate-600 mx-auto mb-3" />
              <p className="text-sm text-slate-400">No invoices yet</p>
              <p className="text-xs text-slate-500 mt-1">Invoices will appear here once you place an order.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-white/6">
                    <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Order</th>
                    <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Issue / Placement</th>
                    <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Date</th>
                    <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Term</th>
                    <th className="px-4 py-3 text-right text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-3 text-center text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider hidden xl:table-cell">Refund / Dispute</th>
                    <th className="px-5 py-3 text-right text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Receipt</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {orders.map((order) => {
                    const badge = paymentBadge[order.paymentStatus] ?? paymentBadge['pending'];
                    return (
                      <tr key={order.id} className="hover:bg-white/2 transition-colors">
                        {/* Order # */}
                        <td className="px-5 py-3.5">
                          <p className="font-semibold text-white">{order.orderNumber}</p>
                          <p className="text-slate-500 mt-0.5 sm:hidden">{order.issueLabel}</p>
                        </td>

                        {/* Issue / Placement */}
                        <td className="px-4 py-3.5 hidden sm:table-cell">
                          <p className="text-slate-300">{order.issueLabel}</p>
                          <p className="text-slate-500 mt-0.5">{PLACEMENT_NAMES[order.placement] || order.placement}</p>
                        </td>

                        {/* Date */}
                        <td className="px-4 py-3.5 hidden md:table-cell text-slate-400">
                          {new Date(order.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </td>

                        {/* Term */}
                        <td className="px-4 py-3.5 hidden lg:table-cell text-slate-400">
                          {order.term === 'three' ? '3-Issue' : 'Single Issue'}
                        </td>

                        {/* Amount */}
                        <td className="px-4 py-3.5 text-right">
                          <span className="font-semibold text-white">${order.total.toLocaleString()}</span>
                          {order.term === 'three' && <span className="text-slate-500">/mo</span>}
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3.5 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold ${badge.className}`}>
                            {badge.icon}
                            {badge.label}
                          </span>
                        </td>

                        {/* Refund / Dispute */}
                        <td className="px-4 py-3.5 hidden xl:table-cell">
                          {order.refundAmount != null && order.refundAmount > 0 ? (
                            <div className="flex items-center gap-1.5">
                              <RotateCcw size={11} className="text-purple-400 shrink-0" />
                              <span className="text-purple-400 font-medium">
                                ${(order.refundAmount / 100).toFixed(2)} refunded
                              </span>
                            </div>
                          ) : order.disputeId ? (
                            <div className="flex items-center gap-1.5">
                              <ShieldAlert size={11} className="text-amber-400 shrink-0" />
                              <span className="text-amber-400 capitalize">
                                Dispute: {order.disputeStatus?.replace(/_/g, ' ') || 'open'}
                              </span>
                            </div>
                          ) : (
                            <span className="text-slate-600">—</span>
                          )}
                        </td>

                        {/* Receipt */}
                        <td className="px-5 py-3.5 text-right">
                          <PDFDownloadButton order={order} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
